// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Typography } from '@mui/material';
import {
  areaElementClasses,
  lineElementClasses,
} from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

import { DateTime } from 'luxon';

const COLORS = {
  good: '#44b700',
  warn: '#ff9800',
  bad: '#f44336',
  offline: '#9e9e9e',
};

// Sensor RSSI thresholds (dBm) — above good = green, between warn/good = orange, below warn = red
const SENSOR_THRESHOLDS = { warn: -70, good: -60 };

function toMs(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Treat RSSI === 0 or null as "no signal" / offline sample.
 */
function normalizeRssi(rssi) {
  if (rssi == null || rssi === 0) return null;
  return Number.isFinite(rssi) ? rssi : null;
}

/**
 * Standalone RSSI sparkline with dynamic threshold-based gradient coloring.
 *
 * @param {{ dataPoints: Array<{Timestamp: string, RSSI: number}>, height?: number, zone?: string, locale?: string }} props
 */
export default function SensorRssiSparkline({
  dataPoints,
  height = 56,
  zone = 'Europe/Copenhagen',
  locale = 'da-dk',
}) {
  const { t } = useTranslation('units-gateways-sensors');

  const points = React.useMemo(
    () => (Array.isArray(dataPoints) ? dataPoints : []),
    [dataPoints],
  );

  const sortedAsc = React.useMemo(
    () =>
      [...points].sort(
        (a, b) => (toMs(a.Timestamp) ?? 0) - (toMs(b.Timestamp) ?? 0),
      ),
    [points],
  );

  const y = React.useMemo(
    () => sortedAsc.map((p) => normalizeRssi(p.RSSI)),
    [sortedAsc],
  );

  const rawTimestamps = React.useMemo(
    () => sortedAsc.map((p) => p.Timestamp),
    [sortedAsc],
  );

  const xLabels = React.useMemo(
    () =>
      rawTimestamps.map((iso) =>
        DateTime.fromISO(iso)
          .setZone(zone)
          .setLocale(locale)
          .toFormat('dd LLL HH:mm'),
      ),
    [rawTimestamps, zone, locale],
  );

  const numeric = React.useMemo(
    () => y.filter((v) => typeof v === 'number' && Number.isFinite(v)),
    [y],
  );

  const worst = React.useMemo(
    () => (numeric.length ? Math.min(...numeric) : null),
    [numeric],
  );

  // Shift RSSI values to be positive (preserves relative ordering for chart)
  const transformedY = React.useMemo(() => {
    if (worst == null) return y;
    return y.map((v) => (v == null ? null : v - worst));
  }, [y, worst]);

  // Keep originals for tooltip
  const originalValues = y;

  const gradientId = React.useId().replace(/:/g, '');

  const gradientStops = React.useMemo(() => {
    if (worst == null) return null;
    const range = Math.max(...numeric) - worst;
    const domainMax = range === 0 ? 1 : range * 1.1;
    const clamp = (v) => Math.min(100, Math.max(0, v));
    const warnPct = clamp(((SENSOR_THRESHOLDS.warn - worst) / domainMax) * 100);
    const goodPct = clamp(((SENSOR_THRESHOLDS.good - worst) / domainMax) * 100);
    return { warnPct, goodPct };
  }, [worst, numeric]);

  const fallbackColor = React.useMemo(() => {
    if (worst == null) return COLORS.offline;
    if (worst >= SENSOR_THRESHOLDS.good) return COLORS.good;
    if (worst >= SENSOR_THRESHOLDS.warn) return COLORS.warn;
    return COLORS.bad;
  }, [worst]);

  const chartColor = gradientStops ? `url(#${gradientId})` : fallbackColor;

  if (!numeric.length) {
    return (
      <Box
        sx={{ width: '100%', display: 'flex', alignItems: 'center', height }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('sensors.noSamples', { defaultValue: 'No signal samples' })}
        </Typography>
      </Box>
    );
  }

  const xAxisOk = xLabels.length === y.length && xLabels.length > 0;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minWidth: 220,
      }}
    >
      {gradientStops && (
        <svg
          width={0}
          height={0}
          aria-hidden
          style={{ position: 'absolute', overflow: 'hidden' }}
        >
          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              y1="1"
              x2="0"
              y2="0"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0%" stopColor={COLORS.bad} />
              <stop
                offset={`${gradientStops.warnPct}%`}
                stopColor={COLORS.bad}
              />
              <stop
                offset={`${gradientStops.warnPct}%`}
                stopColor={COLORS.warn}
              />
              <stop
                offset={`${gradientStops.goodPct}%`}
                stopColor={COLORS.warn}
              />
              <stop
                offset={`${gradientStops.goodPct}%`}
                stopColor={COLORS.good}
              />
              <stop offset="100%" stopColor={COLORS.good} />
            </linearGradient>
          </defs>
        </svg>
      )}
      <SparkLineChart
        height={height}
        color={chartColor}
        area
        curve="monotoneX"
        showTooltip
        showHighlight
        data={transformedY}
        baseline="zero"
        margin={{ bottom: 0, top: 5, left: 4, right: 0 }}
        yAxis={{
          domainLimit: (_min, max) => ({ min: 0, max: max * 1.1 }),
        }}
        {...(xAxisOk ? { xAxis: { scaleType: 'band', data: xLabels } } : {})}
        valueFormatter={(v, { dataIndex }) => {
          const orig = originalValues[dataIndex];
          return orig == null ? '—' : `${orig} dBm`;
        }}
        sx={{
          [`& .${areaElementClasses.root}`]: { opacity: 0.3 },
          [`& .${lineElementClasses.root}`]: { strokeWidth: 2 },
        }}
      />
    </Box>
  );
}
